import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Dialog, DialogClose, IconArrowLeft, IconPlus, ListTable, Notice, Plate, TextField, type ListColumn } from "@wilco/ui";
import { useDeferredValue, useState, type SubmitEvent } from "react";
import { t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { hrApi, type Employee, type Schedule } from "./api.ts";
import { BackToHr } from "./common.tsx";
import { cardNumbersQuery, employeesQuery, PEOPLE } from "./queries.ts";

/** Номер «Картка №» панели дверей по сотруднику: его хранит access, а не hr. */
function useCardNumbers(): Map<string, string> {
  const cards = useQuery(cardNumbersQuery);
  return new Map((cards.data ?? []).flatMap((c) => (c.cardNo ? [[c.employeeId, c.cardNo] as const] : [])));
}

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"] as const;
type Day = (typeof DAYS)[number];

const hhmm = (s: string) => s.slice(0, 5);

/** Краткий график строкой: «Пн–Пт 08:00–17:00». */
function scheduleText(s: Schedule | null | undefined): string {
  if (!s) return t("people.schedule.none");
  const days = DAYS.filter((d) => s.days[d]);
  if (days.length === 0) return s.name;
  const first = s.days[days[0] ?? "MONDAY"];
  return `${s.name} · ${days.map((d) => t(`people.dow.${d}`)).join(" ")}${first ? ` ${hhmm(first.start)}–${hhmm(first.end)}` : ""}`;
}

/**
 * «Співробітники»: бейдж EM для киоска и терминалов, «Картка №» панели
 * дверей, график. Сотрудник без учётки здесь тоже есть, он только
 * отмечается (КД-12).
 */
export function EmployeesPage() {
  const navigate = useNavigate();
  const employees = useQuery(employeesQuery);
  const cards = useCardNumbers();
  const [text, setText] = useState("");
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const q = useDeferredValue(text.trim().toLowerCase());
  const rows = (employees.data ?? [])
    .filter((e) => e.archived === archived)
    .filter((e) => !q || e.name.toLowerCase().includes(q) || (e.card ?? "").includes(q) || (cards.get(e.id) ?? "").includes(q))
    .sort((a, b) => a.name.localeCompare(b.name, "uk"));

  const columns: ListColumn<Employee>[] = [
    { key: "name", header: t("people.name"), kind: "title", render: (e) => e.name },
    { key: "badge", header: t("people.badge"), kind: "text", width: "9rem", render: (e) => <Labeled label={t("people.badge")} value={e.card} /> },
    { key: "card", header: t("people.cardNo"), kind: "text", width: "7rem", render: (e) => <Labeled label={t("people.cardNo")} value={cards.get(e.id)} /> },
    { key: "schedule", header: t("people.schedule"), kind: "text", render: (e) => scheduleText(e.schedule) },
    { key: "user", header: t("people.account"), kind: "text", width: "9rem", render: (e) => (e.userId ? t("people.account.linked") : t("people.account.none")) },
  ];

  return (
    <div className="page people">
      <div className="page-head">
        <BackToHr />
        <h1 className="page-title">{t("people.employees")}</h1>
        <Button variant="create" onClick={() => setCreating(true)}>
          <IconPlus />
          {t("people.new")}
        </Button>
      </div>
      <form role="search" className="people-filters" onSubmit={(e) => e.preventDefault()}>
        <TextField label={t("people.search")} placeholder={t("people.searchHint")} value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
        <label className="check">
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          {t("people.showArchived")}
        </label>
      </form>
      {employees.error ? <Notice tone="danger" title={errorNotice(employees.error).title} className="stock-notice" /> : null}
      <ListTable
        label={t("people.employees")}
        columns={columns}
        rows={rows}
        rowKey={(e) => e.id}
        empty={employees.isPending ? null : t("people.empty")}
        onRowClick={(e) => void navigate({ to: "/hr/employees/$id", params: { id: e.id } })}
      />
      <NewEmployeeDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

/** Номер с подписью: на ПК подпись в шапке колонки, на телефоне перед числом в карточке. */
function Labeled({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <span className="people-compact-label">{label} </span>
      <span className="w-num">{value ?? "—"}</span>
    </>
  );
}

function NewEmployeeDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [badge, setBadge] = useState("");
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const create = useMutation({ mutationFn: () => hrApi.create(name.trim(), badge.trim() || null) });
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    create.mutate(undefined, {
      onSuccess: (saved) => {
        void queryClient.invalidateQueries({ queryKey: PEOPLE });
        onOpenChange(false);
        setName("");
        setBadge("");
        void navigate({ to: "/hr/employees/$id", params: { id: saved.id } });
      },
      onError: (err) => setNotice(errorNotice(err)),
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("people.new")}
      footer={
        <>
          <Button variant="primary" type="submit" form="new-employee" disabled={create.isPending || !name.trim()}>
            {t("people.create")}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">{t("common.cancel")}</Button>
          </DialogClose>
        </>
      }
    >
      <form id="new-employee" className="stock-form" onSubmit={submit}>
        {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
        <TextField label={t("people.name")} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
        <TextField label={t("people.badge")} hint={t("people.badgeHint")} inputMode="numeric" value={badge} onChange={(e) => setBadge(e.target.value)} autoComplete="off" />
      </form>
    </Dialog>
  );
}

const route = getRouteApi("/app/hr/employees/$id");

/** Карточка сотрудника: имя, бейдж, «Картка №», PIN киоска, график, архив. */
export function EmployeePage() {
  const { id } = route.useParams();
  const employees = useQuery(employeesQuery);
  // Поле «Картка №» берёт начальное значение при монтировании: ждём список номеров.
  const cards = useQuery(cardNumbersQuery);
  const employee = employees.data?.find((e) => e.id === id);
  if (cards.isPending) return null;
  if (!employee) {
    if (employees.error) return <Notice tone="danger" title={errorNotice(employees.error).title} className="page" />;
    return employees.isPending ? null : <Notice tone="danger" title={t("people.notFound")} className="page" />;
  }
  return <EmployeeCard key={employee.id} employee={employee} />;
}

function EmployeeCard({ employee }: { employee: Employee }) {
  const queryClient = useQueryClient();
  const cards = useCardNumbers();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [name, setName] = useState(employee.name);
  const [badge, setBadge] = useState(employee.card ?? "");
  const [cardNo, setCardNo] = useState(cards.get(employee.id) ?? "");
  const [pin, setPin] = useState("");
  const done = (title: string) => ({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEOPLE });
      setNotice({ tone: "success", title });
    },
    onError: (err: unknown) => setNotice(errorNotice(err)),
  });
  const save = useMutation({
    mutationFn: async () => {
      if (name.trim() !== employee.name) await hrApi.rename(employee.id, name.trim());
      if (badge.trim() !== (employee.card ?? "")) await hrApi.badge(employee.id, badge.trim() || null);
      if (cardNo.trim() !== (cards.get(employee.id) ?? "")) await hrApi.setCardNumber(employee.id, cardNo.trim() || null);
    },
  });
  const savePin = useMutation({ mutationFn: (v: string | null) => hrApi.pin(employee.id, v) });
  const archive = useMutation({ mutationFn: () => hrApi.archive(employee.id, !employee.archived) });

  return (
    <div className="page stock-narrow">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/hr/employees" aria-label={t("people.backToList")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title">{employee.name}</h1>
        {employee.archived ? <Badge>{t("people.archived")}</Badge> : null}
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      <Plate cut="lg">
        <form
          className="stock-form"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            save.mutate(undefined, done(t("people.saved")));
          }}
        >
          <TextField label={t("people.name")} value={name} onChange={(e) => setName(e.target.value)} required />
          <TextField label={t("people.badge")} hint={t("people.badgeHint")} inputMode="numeric" value={badge} onChange={(e) => setBadge(e.target.value)} autoComplete="off" />
          <TextField label={t("people.cardNo")} hint={t("people.cardNoHint")} inputMode="numeric" value={cardNo} onChange={(e) => setCardNo(e.target.value)} autoComplete="off" />
          <p className="w-muted">{employee.userId ? t("people.account.linkedLong") : t("people.account.noneLong")}</p>
          <Button type="submit" variant="primary" disabled={save.isPending || !name.trim()}>
            {t("order.save")}
          </Button>
        </form>
      </Plate>

      <h2 className="people-h2">{t("people.pin")}</h2>
      <Plate cut="md">
        <form
          className="people-inline"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            savePin.mutate(pin, { ...done(t("people.pinSaved")), onSettled: () => setPin("") });
          }}
        >
          <TextField
            label={t("people.pinNew")}
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
          <Button type="submit" disabled={savePin.isPending || pin.length < 4}>
            {t("people.pinSet")}
          </Button>
          <Button variant="ghost" disabled={savePin.isPending} onClick={() => savePin.mutate(null, done(t("people.pinCleared")))}>
            {t("people.pinClear")}
          </Button>
        </form>
        <p className="w-muted people-caption">{t("people.pinHint")}</p>
      </Plate>

      <h2 className="people-h2">{t("people.schedule")}</h2>
      <ScheduleEditor employee={employee} onNotice={setNotice} />

      <div className="order-actions">
        <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate(undefined, done(employee.archived ? t("people.restored") : t("people.archivedDone")))}>
          {employee.archived ? t("people.restore") : t("people.archive")}
        </Button>
      </div>
    </div>
  );
}

interface DayRow {
  on: boolean;
  start: string;
  end: string;
  breakStart: string;
  breakEnd: string;
}

function rowsOf(s: Schedule | null | undefined): Record<Day, DayRow> {
  const out = {} as Record<Day, DayRow>;
  for (const d of DAYS) {
    const day = s?.days[d];
    const br = day?.breaks[0];
    out[d] = {
      on: Boolean(day),
      start: day ? hhmm(day.start) : "08:00",
      end: day ? hhmm(day.end) : "17:00",
      breakStart: br ? hhmm(br.start) : "",
      breakEnd: br ? hhmm(br.end) : "",
    };
  }
  return out;
}

/**
 * Недельный график: день без галочки нерабочий, перерыв один (обед). По
 * нему сервер считает переработку дня (H13), поэтому график правит тот,
 * кто ведёт табель.
 */
function ScheduleEditor({ employee, onNotice }: { employee: Employee; onNotice: (n: NoticeState | null) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(employee.schedule?.name ?? t("people.schedule.default"));
  const [rows, setRows] = useState(() => rowsOf(employee.schedule));
  const save = useMutation({ mutationFn: (s: Schedule | null) => hrApi.schedule(employee.id, s) });
  const set = (d: Day, patch: Partial<DayRow>) => setRows((r) => ({ ...r, [d]: { ...r[d], ...patch } }));

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    onNotice(null);
    const days: Schedule["days"] = {};
    for (const d of DAYS) {
      const r = rows[d];
      if (!r.on) continue;
      days[d] = { start: r.start, end: r.end, breaks: r.breakStart && r.breakEnd ? [{ start: r.breakStart, end: r.breakEnd }] : [] };
    }
    save.mutate(
      { name: name.trim() || t("people.schedule.default"), days },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: PEOPLE });
          onNotice({ tone: "success", title: t("people.scheduleSaved") });
        },
        onError: (err) => onNotice(errorNotice(err)),
      },
    );
  };

  return (
    <Plate cut="md">
      <form className="stock-form" onSubmit={submit}>
        <TextField label={t("people.schedule.name")} value={name} onChange={(e) => setName(e.target.value)} />
        <div className="people-week" role="group" aria-label={t("people.schedule")}>
          {DAYS.map((d) => (
            <div key={d} className="people-week-row" data-off={rows[d].on ? undefined : ""}>
              <label className="check people-week-day">
                <input type="checkbox" checked={rows[d].on} onChange={(e) => set(d, { on: e.target.checked })} />
                {t(`people.dow.${d}`)}
              </label>
              <TextField label={t("people.schedule.start")} type="time" value={rows[d].start} disabled={!rows[d].on} onChange={(e) => set(d, { start: e.target.value })} />
              <TextField label={t("people.schedule.end")} type="time" value={rows[d].end} disabled={!rows[d].on} onChange={(e) => set(d, { end: e.target.value })} />
              <TextField label={t("people.schedule.breakStart")} type="time" value={rows[d].breakStart} disabled={!rows[d].on} onChange={(e) => set(d, { breakStart: e.target.value })} />
              <TextField label={t("people.schedule.breakEnd")} type="time" value={rows[d].breakEnd} disabled={!rows[d].on} onChange={(e) => set(d, { breakEnd: e.target.value })} />
            </div>
          ))}
        </div>
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {t("people.scheduleSave")}
        </Button>
      </form>
    </Plate>
  );
}
